/*
 * Deterministic layout for the Power map lens.
 *
 * Each influence area is a circular region. An entity appears once in every area where it holds a
 * score; its distance from the region centre encodes the score (100 at the centre, 0 at the edge),
 * so the dominant actors of an area literally sit at its core. Angles come from a hash of the
 * entity id, so positions stay put when filters change and only collisions shift nodes.
 * Unassessed entities get a neutral region, placed by network position instead of score.
 */
import { InfluenceArea, InfluenceEntity, UNASSESSED_REGION } from './influenceModel';

export interface MapRegion {
  id: string;
  label: string;
  color: string | null;
  cx: number;
  cy: number;
  /** outer radius */
  R: number;
  /** radius of the score scale (score 0 sits here) */
  scaleR: number;
  count: number;
}

export interface MapInstance {
  /** `${entityId}:${regionId}` */
  key: string;
  entityId: string;
  regionId: string;
  /** Area score, or network percentile in the unassessed region */
  score: number;
  x: number;
  y: number;
  r: number;
}

export interface MapLayout {
  regions: MapRegion[];
  instances: MapInstance[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export const nodeRadius = (score: number) => 5.5 + (Math.max(0, Math.min(100, score)) / 100) * 13;

const PAD = 2.5;
const MIN_R = 120;
const GAP_X = 90;
const GAP_Y = 120;

/** FNV-1a hash → [0, 1) */
export function hashUnit(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 4294967296;
}

interface Item {
  entityId: string;
  score: number;
}

function regionRadius(items: Item[]): number {
  let sum = 0;
  items.forEach((it) => {
    const r = nodeRadius(it.score) + PAD;
    sum += r * r;
  });
  return Math.max(MIN_R, Math.sqrt(sum) * 2.1 + 24);
}

/** Places nodes around the origin; returns positions relative to the region centre */
function placeInRegion(items: Item[], scaleR: number): Array<{ x: number; y: number; r: number }> {
  const cell = 2 * (nodeRadius(100) + PAD);
  // numeric cell keys: string keys dominated layout time on large maps
  const grid = new Map<number, number[]>();
  const xs: number[] = [];
  const ys: number[] = [];
  const rs: number[] = [];
  const cellKey = (gx: number, gy: number) => (gx + 5000) * 10007 + (gy + 5000);
  const fits = (x: number, y: number, r: number) => {
    const gx = Math.floor(x / cell);
    const gy = Math.floor(y / cell);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const list = grid.get(cellKey(gx + dx, gy + dy));
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const j = list[k];
          const min = rs[j] + r + PAD;
          const ddx = xs[j] - x;
          const ddy = ys[j] - y;
          if (ddx * ddx + ddy * ddy < min * min) return false;
        }
      }
    }
    return true;
  };
  const add = (x: number, y: number, r: number) => {
    xs.push(x);
    ys.push(y);
    rs.push(r);
    const key = cellKey(Math.floor(x / cell), Math.floor(y / cell));
    const list = grid.get(key);
    if (list) list.push(xs.length - 1);
    else grid.set(key, [xs.length - 1]);
  };

  const out: Array<{ x: number; y: number; r: number }> = new Array(items.length);
  const order = items.map((_, i) => i).sort((a, b) => items[b].score - items[a].score || (items[a].entityId < items[b].entityId ? -1 : 1));
  order.forEach((i) => {
    const it = items[i];
    const r = nodeRadius(it.score);
    const theta0 = hashUnit(it.entityId) * Math.PI * 2;
    let d = scaleR * (1 - Math.max(0, Math.min(100, it.score)) / 100);
    let spot: { x: number; y: number } | null = null;
    for (let ring = 0; ring < 400 && !spot; ring++) {
      // More candidate angles on larger circles
      const steps = Math.max(6, Math.min(56, Math.ceil((2 * Math.PI * Math.max(d, 1)) / (r * 1.5))));
      for (let k = 0; k < steps; k++) {
        const offset = (k % 2 === 1 ? 1 : -1) * Math.ceil(k / 2);
        const theta = theta0 + (offset * 2 * Math.PI) / steps;
        const x = Math.cos(theta) * d;
        const y = Math.sin(theta) * d;
        if (fits(x, y, r)) {
          spot = { x, y };
          break;
        }
      }
      if (!spot) d += Math.max(2.5, r * 0.6);
    }
    const p = spot || { x: Math.cos(theta0) * d, y: Math.sin(theta0) * d };
    add(p.x, p.y, r);
    out[i] = { x: p.x, y: p.y, r };
  });
  return out;
}

export interface LayoutInput {
  areas: InfluenceArea[];
  /** Assessed entities in scope */
  entities: InfluenceEntity[];
  areaIds: Set<string>;
  minScore: number;
  /** Unassessed entities with their network percentile */
  unassessed: Array<{ entity: InfluenceEntity; weight: number }>;
  /** viewport width / height, to arrange regions to fill the screen */
  aspect: number;
}

export function layoutPowerMap(input: LayoutInput): MapLayout {
  const groups: Array<{ id: string; label: string; color: string | null; items: Item[] }> = [];
  input.areas.forEach((area) => {
    if (!input.areaIds.has(area.id)) return;
    const items: Item[] = [];
    input.entities.forEach((e) => {
      const s = e.scoreByArea[area.id];
      if (s && s.score >= input.minScore) items.push({ entityId: e.id, score: s.score });
    });
    groups.push({ id: area.id, label: area.label, color: area.color, items });
  });
  if (input.unassessed.length) {
    groups.push({
      id: UNASSESSED_REGION,
      label: 'Unassessed',
      color: null,
      items: input.unassessed.map((u) => ({ entityId: u.entity.id, score: u.weight })),
    });
  }
  if (!groups.length) return { regions: [], instances: [], bounds: { minX: -100, minY: -100, maxX: 100, maxY: 100 } };

  const radii = groups.map((g) => regionRadius(g.items));
  const n = groups.length;
  const aspect = Number.isFinite(input.aspect) && input.aspect > 0 ? input.aspect : 1.6;

  // Pick the column count whose grid best fills a viewport of this aspect ratio
  const maxR = Math.max(...radii);
  let bestCols = 1;
  let bestScore = -Infinity;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const width = cols * (2 * maxR + GAP_X);
    const height = rows * (2 * maxR + GAP_Y);
    const score = Math.min(aspect / width, 1 / height) - (cols * rows - n) * 1e-7;
    if (score > bestScore) {
      bestScore = score;
      bestCols = cols;
    }
  }

  const regions: MapRegion[] = [];
  const instances: MapInstance[] = [];
  const rowCount = Math.ceil(n / bestCols);
  let y = 0;
  for (let row = 0; row < rowCount; row++) {
    const slice = groups.slice(row * bestCols, (row + 1) * bestCols);
    const sliceR = radii.slice(row * bestCols, (row + 1) * bestCols);
    const rowHeight = 2 * Math.max(...sliceR);
    const cellW = 2 * maxR + GAP_X;
    const rowWidth = slice.length * cellW;
    const fullWidth = bestCols * cellW;
    const rowCentre = y + rowHeight / 2;
    slice.forEach((g, i) => {
      const R = sliceR[i];
      const cx = (fullWidth - rowWidth) / 2 + cellW / 2 + i * cellW;
      const cy = rowCentre;
      const scaleR = R - nodeRadius(60) - 4;
      regions.push({ id: g.id, label: g.label, color: g.color, cx, cy, R, scaleR, count: g.items.length });
      const pos = placeInRegion(g.items, scaleR);
      g.items.forEach((it, k) => {
        instances.push({
          key: `${it.entityId}:${g.id}`,
          entityId: it.entityId,
          regionId: g.id,
          score: it.score,
          x: cx + pos[k].x,
          y: cy + pos[k].y,
          r: pos[k].r,
        });
      });
    });
    y += rowHeight + GAP_Y;
  }

  // Center on the origin
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  regions.forEach((r) => {
    minX = Math.min(minX, r.cx - r.R);
    maxX = Math.max(maxX, r.cx + r.R);
    minY = Math.min(minY, r.cy - r.R - 40); // room for the label above
    maxY = Math.max(maxY, r.cy + r.R);
  });
  instances.forEach((p) => {
    minX = Math.min(minX, p.x - p.r);
    maxX = Math.max(maxX, p.x + p.r);
    minY = Math.min(minY, p.y - p.r);
    maxY = Math.max(maxY, p.y + p.r);
  });
  const ox = (minX + maxX) / 2;
  const oy = (minY + maxY) / 2;
  regions.forEach((r) => {
    r.cx -= ox;
    r.cy -= oy;
  });
  instances.forEach((p) => {
    p.x -= ox;
    p.y -= oy;
  });
  return {
    regions,
    instances,
    bounds: { minX: minX - ox, minY: minY - oy, maxX: maxX - ox, maxY: maxY - oy },
  };
}

/* ---------------- Camera ---------------- */

export interface Camera {
  /** world point at the viewport centre */
  x: number;
  y: number;
  k: number;
}

export const MIN_ZOOM = 0.15;
export const MAX_ZOOM = 6;
export const clampZoom = (k: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, k));

export function fitCamera(bounds: MapLayout['bounds'], width: number, height: number, padding = 48): Camera {
  const w = Math.max(1, bounds.maxX - bounds.minX);
  const h = Math.max(1, bounds.maxY - bounds.minY);
  const k = clampZoom(Math.min((width - padding * 2) / w, (height - padding * 2) / h));
  return { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2, k: Number.isFinite(k) ? k : 1 };
}

export const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
export const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** Camera flight: centre moves smoothly, zoom is geometric with a gentle pull-back on long trips */
export function interpolateCamera(a: Camera, b: Camera, t: number, viewport: number): Camera {
  const e = easeInOutCubic(t);
  const dist = Math.hypot(b.x - a.x, b.y - a.y) * Math.min(a.k, b.k);
  const dip = Math.min(0.45, dist / Math.max(1, viewport) / 3);
  const k = a.k * Math.pow(b.k / a.k, e) * (1 - dip * Math.sin(Math.PI * e));
  return { x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e, k };
}
